import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useAppDispatch } from '../../store';
import { useTranslation } from 'react-i18next';
import { BaseForm, BaseFormFieldConfig, BaseFormSectionConfig } from '../shared/FormComponents';
import { FormSchema, FormField } from '../../utils/validation/form-schema';
import TagInput from '../shared/TailwindComponents/TagInput';
import DetailFrame from '../shared/Frames/DetailFrame';
import { createReview } from '../../store/actions/Review/ReviewActions';
import { fetchLinkableProjects } from '../../store/actions/Projects/ProjectActions';
import { DropdownOption } from '../shared/TailwindComponents/Dropdown';

// ─── TagInput adapter for BaseForm custom field ─────────────────────────────

interface TagFieldProps {
  id?: string;
  name?: string;
  label?: string;
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
}

const TagField: React.FC<TagFieldProps> = ({
  id = 'tags',
  label = '',
  value = [],
  onChange,
  placeholder,
}) => (
  <TagInput
    id={id}
    label={label}
    placeholder={placeholder}
    tags={value || []}
    onChange={onChange}
  />
);

// ─── Component ───────────────────────────────────────────────────────────────

const CreateReviewForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const [projectOptions, setProjectOptions] = useState<DropdownOption[]>([]);

  useEffect(() => {
    const loadProjects = async () => {
      try {
        const projectsResp = await dispatch(fetchLinkableProjects() as any);
        if (projectsResp && Array.isArray(projectsResp)) {
          setProjectOptions(projectsResp.map((p: any) => ({ value: p.uuid, label: p.title })));
        }
      } catch (error: any) {
        console.error('[CreateReviewForm] Error loading projects:', error?.message);
      }
    };
    loadProjects();
  }, [dispatch]);

  const breadcrumbs = useMemo(() => [
    { name: t('Playbooks'), href: '/playbooks', current: false },
    { name: t('New Playbook'), href: '/playbooks/new', current: true },
  ], [t]);

  const schema = useMemo(() => new FormSchema('createReview', [
    new FormField('name', 'title', true, t('Playbook Name'), { minLength: 2, maxLength: 255 }),
    new FormField('description', 'text', false, t('Description')),
    new FormField('agreement_types', 'text', false, t('Agreement Types'), {
      customValidator: () => ({ isValid: true }),
    }),
    new FormField('jurisdictions', 'text', false, t('Jurisdictions'), {
      customValidator: () => ({ isValid: true }),
    }),
  ]), [t]);

  const sections: BaseFormSectionConfig[] = useMemo(() => [
    {
      title: t('Playbook Details'),
      description: t('Name the playbook and describe what it checks for.'),
      fields: [
        {
          name: 'name',
          type: 'input',
          label: t('Playbook Name'),
          placeholder: t('e.g. NDA Review Standard, M&A Due Diligence'),
          required: true,
          gridSize: 'full',
        } as BaseFormFieldConfig,
        {
          name: 'description',
          type: 'textarea',
          label: t('Description'),
          placeholder: t('Describe what this playbook checks for...'),
          rows: 3,
          gridSize: 'full',
        } as BaseFormFieldConfig,
      ],
    },
    {
      title: t('Classification'),
      description: t('Categorize this playbook to help organize and filter later.'),
      gridCols: 2,
      fields: [
        {
          name: 'agreement_types',
          type: 'custom',
          label: t('Agreement Types'),
          gridSize: 'half',
          customComponent: TagField,
          customProps: {
            placeholder: t('Type and press Enter (e.g. NDA, SPA, Employment)'),
          },
        } as BaseFormFieldConfig,
        {
          name: 'jurisdictions',
          type: 'custom',
          label: t('Jurisdictions'),
          gridSize: 'half',
          customComponent: TagField,
          customProps: {
            placeholder: t('Type and press Enter (e.g. Norway, EU, US-NY)'),
          },
        } as BaseFormFieldConfig,
      ],
    },
    ...(projectOptions.length > 0 ? [{
      title: t('Link to Project'),
      description: t('Optionally link this playbook to an existing project.'),
      fields: [
        {
          name: 'project_uuid',
          type: 'dropdown' as const,
          label: t('Project'),
          placeholder: t('Select a project'),
          required: false,
          gridSize: 'full' as const,
          customProps: { fullWidth: true } as any,
          options: projectOptions,
        } as BaseFormFieldConfig,
      ],
    }] : []),
  ], [t, projectOptions]);

  const handleSubmit = useCallback(async (formData: Record<string, any>) => {
    const payload: any = {
      name: formData.name,
      description: formData.description || undefined,
      agreement_types: formData.agreement_types || [],
      jurisdictions: formData.jurisdictions || [],
    };
    if (formData.project_uuid) {
      payload.project_uuid = formData.project_uuid;
    }
    const result = await dispatch(createReview(payload) as any);

    if (result?.uuid) {
      history.replace(`/playbooks/${result.uuid}`);
    }
  }, [dispatch, history]);

  const handleCancel = useCallback(() => {
    history.push('/playbooks');
  }, [history]);

  return (
    <DetailFrame
      breadcrumbs={breadcrumbs}
      actions={[]}
    >
      <BaseForm
        title={t('Create Playbook')}
        description={t('Set up a new playbook with your firm\'s standards and rules.')}
        sections={sections}
        schema={schema}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        submitLabel={t('Create Playbook')}
        cancelLabel={t('Cancel')}
        maxWidth="max-w-3xl"
      />
    </DetailFrame>
  );
};

export default CreateReviewForm;
